import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Send,
  BadgeCheck,
  Download,
  Copy,
  Check,
  Loader2,
  QrCode,
} from "lucide-react";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { catalogService } from "../../../services/catalogService";
import { companiesService } from "../../../services/companiesService";
import { toast } from "sonner";
import { downloadBarcodePng, downloadQrPng } from "../../../utils/codeImages";
import { RepEditorModal } from "../../components/RepEditorModal";
import type { Representative } from "../../../types";

export function ExhibitorRepsPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [viewRep, setViewRep] = useState<Representative | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [reps, setReps] = useState<Representative[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);

  const eventId = user?.assignedEventId ?? "";
  const companyId = user?.assignedCompanyId ?? "";

  const loadReps = useCallback(async () => {
    if (!eventId || !companyId) return;
    setLoading(true);
    try {
      const [list, company] = await Promise.all([
        catalogService.listRepresentatives(eventId, companyId, "admin"),
        companiesService.getById(companyId, eventId, "admin"),
      ]);
      setReps(list);
      setCompanyName(company?.name ?? "");
    } catch {
      toast.error("Не удалось загрузить представителей");
    } finally {
      setLoading(false);
    }
  }, [eventId, companyId]);

  useEffect(() => {
    void loadReps();
  }, [loadReps]);

  const handleCopyQr = (code: string, id: string) => {
    void navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadBadge = async (rep: Representative) => {
    setDownloadingId(rep.id);
    try {
      if (rep.badgeQrCode) {
        await downloadQrPng(rep.badgeQrCode, `${rep.fullName}-badge-qr`);
      }
      if (rep.badgeBarcode) {
        downloadBarcodePng(rep.badgeBarcode, `${rep.fullName}-badge`);
      }
      if (!rep.badgeQrCode && !rep.badgeBarcode) {
        toast.error("Код бейджа ещё не создан");
        return;
      }
      toast.success("Бейдж сохранён");
    } catch {
      toast.error("Не удалось скачать бейдж");
    } finally {
      setDownloadingId(null);
    }
  };

  if (!companyId) return <div className="p-6 text-gray-500">Компания не найдена</div>;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-5">
      {viewRep && (
        <RepEditorModal
          mode="view"
          readOnly
          initial={viewRep}
          onClose={() => setViewRep(null)}
          onSave={async () => undefined}
        />
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/exhibitor")}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hidden sm:block"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-[19px] text-gray-900">Представители</h1>
          <p className="text-[12px] text-gray-400">
            {companyName || "Стенд"} · {reps.length} чел. · Только просмотр и QR
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/exhibitor/qr")}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-200 text-indigo-600 text-[13px] hover:bg-indigo-50"
        >
          <QrCode size={14} /> QR
        </button>
      </div>

      <div className="p-3.5 rounded-xl bg-indigo-50 border border-indigo-100 text-[12px] text-indigo-700 leading-relaxed">
        Списки и контакты сотрудников сформированы из системы сбора информации о стендистах.
        Добавление новых сотрудников — только через организатора выставки. Вы скачиваете личные
        QR-бейджи: посетитель сканирует и видит визитку с контактами.
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-gray-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : reps.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 py-16 text-center">
          <User size={32} className="text-gray-200 mx-auto mb-3" />
          <p className="text-[14px] text-gray-500">Представителей ещё нет</p>
          <p className="text-[13px] text-gray-400 mt-1">Добавление — только через организатора выставки</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reps.map((rep) => (
            <div key={rep.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-start gap-3">
                {rep.photo ? (
                  <img
                    src={rep.photo}
                    alt={rep.fullName}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <User size={22} className="text-indigo-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <button
                    type="button"
                    className="text-left w-full"
                    onClick={() => setViewRep(rep)}
                  >
                    <div className="text-[14px] text-gray-900">{rep.fullName}</div>
                    <div className="text-[12px] text-indigo-600">
                      {rep.position || "Должность не указана"}
                    </div>
                  </button>
                  <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-gray-500">
                    <span className="flex items-center gap-1">
                      <Phone size={10} /> {rep.phone || "телефон не указан"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail size={10} /> {rep.email || "email не указан"}
                    </span>
                    {rep.telegram ? (
                      <span className="flex items-center gap-1 text-sky-500">
                        <Send size={10} /> {rep.telegram}
                      </span>
                    ) : null}
                  </div>
                  {rep.bio ? (
                    <p className="text-[12px] text-gray-400 mt-1.5 leading-snug">{rep.bio}</p>
                  ) : null}
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <BadgeCheck size={14} className="text-emerald-500 flex-shrink-0" />
                  <span className="text-[11px] text-gray-500">Бейдж</span>
                  <span className="font-mono text-[10px] text-gray-300 truncate max-w-[120px]">
                    {rep.badgeQrCode || "—"}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleCopyQr(rep.badgeQrCode, rep.id)}
                    disabled={!rep.badgeQrCode}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-50 hover:bg-indigo-50 text-gray-500 hover:text-indigo-600 text-[11px] disabled:opacity-40"
                  >
                    {copiedId === rep.id ? <Check size={11} /> : <Copy size={11} />}
                    {copiedId === rep.id ? "OK" : "QR"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDownloadBadge(rep)}
                    disabled={downloadingId === rep.id}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[11px] disabled:opacity-60"
                  >
                    {downloadingId === rep.id ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      <Download size={11} />
                    )}
                    Бейдж
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
